import { consoleUserType } from '@constants_common';

// C0 controls and DEL. The URL parser silently drops tab / newline and trims the others,
// which would let '/\t/evil.example' turn into '//evil.example' after parsing.
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;
// '.' / '..' including the percent-encoded forms the URL parser also resolves as dot segments.
const DOT_SEGMENT = /^(?:\.|%2e){1,2}$/i;
const ENCODED_SEPARATORS = /%2f|%5c/i;
// The URL parser leaves '[' / ']' unescaped in paths, and next/router treats a pathname containing them as a
// dynamic route template: it interpolates query values into it ('[...slug]?slug=..&slug=..' leaves the prefix)
// or rejects the push when values are missing. Encoded '%5B' / '%5D' are not matched as templates.
const DYNAMIC_ROUTE_BRACKETS = /[[\]]/;

/**
 * Returns the root-relative path to push after login, or null when the redirect query must not be followed.
 *
 * `redirect` is `router.query.redirect` as is: ProtectedRoutes encodes `router.asPath` once and Next.js
 * decodes the query once, so it must not be decoded again (that corrupts '%25' and can throw).
 * Only paths under `/console/<userType>/` on `origin` are allowed, excluding the login page itself.
 */
export const getValidatedLoginRedirect = (
  redirect: string | string[] | undefined,
  userType: consoleUserType,
  origin: string,
): string | null => {
  if (typeof redirect !== 'string' || redirect === '') return null;

  // A single leading '/' and no backslash keep schemes (javascript:, data:, https:) and authorities
  // ('//host', '/\host') out before the URL parser gets a chance to interpret them.
  if (!redirect.startsWith('/') || redirect.startsWith('//')) return null;
  if (redirect.includes('\\') || CONTROL_CHARACTERS.test(redirect)) return null;

  const rawPathname = redirect.split(/[?#]/, 1)[0];
  if (rawPathname.split('/').some((segment) => DOT_SEGMENT.test(segment))) return null;

  try {
    const baseOrigin = new URL(origin).origin;
    const url = new URL(redirect, baseOrigin);
    if (url.origin !== baseOrigin) return null;

    const { pathname } = url;
    if (!pathname.startsWith(`/console/${userType}/`)) return null;
    if (pathname.includes('//') || ENCODED_SEPARATORS.test(pathname)) return null;
    if (DYNAMIC_ROUTE_BRACKETS.test(pathname)) return null;

    // Next.js decodes dynamic route params on push, so an undecodable path would make the navigation throw.
    const decodedPathname = decodeURIComponent(pathname);
    if (CONTROL_CHARACTERS.test(decodedPathname)) return null;

    const loginPathname = `/console/${userType}/login`;
    if (decodedPathname === loginPathname || decodedPathname === `${loginPathname}/`) return null;

    // ProtectedRoutes does not attach another redirect query when hasRedirected=true, which prevents a redirect loop.
    url.searchParams.set('hasRedirected', 'true');

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
};
